import { fetchPayments } from "services/paymentsService/paymentsService";
import type { Payment } from "services/paymentsService/paymentsService.types";
import { fetchStudents } from "services/studentService/studentService";
import type { StudentPurchase } from "services/studentService/studentService.types";
import { logger } from "utils/logger";
import type { StatisticResponse } from "./statisticService.types";

const PAGE_SIZE = 500;
const DAY_MS = 24 * 60 * 60 * 1000;

const toDateKey = (value: string): string => {
  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const subscriptionDaysForPayment = (payment: Payment): number => {
  const created = new Date(payment.created);
  const endsAt = new Date(payment.subscription_end);
  const diff = endsAt.getTime() - created.getTime();
  if (!Number.isFinite(diff) || diff <= 0) {
    return 0;
  }
  return Math.round(diff / DAY_MS);
};

const fetchAllPurchases = async (): Promise<StudentPurchase[]> => {
  const all: StudentPurchase[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;

  while (all.length < total) {
    const response = await fetchStudents(page, PAGE_SIZE);
    const batch = response.results.results;
    total = response.count;
    all.push(...batch);
    if (!batch.length) {
      break;
    }
    page += 1;
  }

  return all;
};

const fetchAllPayments = async (): Promise<Payment[]> => {
  const all: Payment[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;

  while (all.length < total) {
    const response = await fetchPayments(page, PAGE_SIZE, "created");
    const batch = response.results.payments;
    total = response.count;
    all.push(...batch);
    if (!batch.length) {
      break;
    }
    page += 1;
  }

  return all;
};

interface DailyAccumulator {
  created: string;
  new_users: number;
  total_income: number;
  subscription_days: number;
}

export const buildStatisticSeries = (
  purchases: StudentPurchase[],
  payments: Payment[],
): StatisticResponse[] => {
  const daily = new Map<string, DailyAccumulator>();
  const ensureDay = (key: string) => {
    const current = daily.get(key);
    if (current) {
      return current;
    }
    const created: DailyAccumulator = {
      created: key,
      new_users: 0,
      total_income: 0,
      subscription_days: 0,
    };
    daily.set(key, created);
    return created;
  };

  const sortedPurchases = [...purchases].sort(
    (left, right) => new Date(left.created).getTime() - new Date(right.created).getTime(),
  );
  const seenStudents = new Set<string>();

  sortedPurchases.forEach((purchase) => {
    const key = toDateKey(purchase.created);
    const bucket = ensureDay(key);
    const studentId = String(purchase.student.tg_id);
    if (!seenStudents.has(studentId)) {
      seenStudents.add(studentId);
      bucket.new_users += 1;
    }
  });

  payments.forEach((payment) => {
    const key = toDateKey(payment.created);
    const bucket = ensureDay(key);
    bucket.total_income += Number(payment.price) || 0;
    bucket.subscription_days += subscriptionDaysForPayment(payment);
  });

  let totalUsers = 0;
  return [...daily.values()]
    .sort((left, right) => left.created.localeCompare(right.created))
    .map((bucket) => {
      totalUsers += bucket.new_users;
      return {
        ...bucket,
        total_users: totalUsers,
      };
    });
};

export const fetchStatistics = async (): Promise<StatisticResponse[]> => {
  try {
    const [purchases, payments] = await Promise.all([
      fetchAllPurchases(),
      fetchAllPayments(),
    ]);
    return buildStatisticSeries(purchases, payments);
  } catch (error) {
    logger.error("Ошибка при получении статистики:", error);
    throw error;
  }
};
